import { ActionIcon, Center, Flex, Stack, Text } from '@mantine/core';
import { IconEdit as EditIcon } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { getResearchPerfEvalItemFileDownloadUrl } from '@/module/ResearchPerfEval/model/ResearchPerfEvalApi';
import { useResearchPerfEvalDeleteCriteriaItem } from '@/module/ResearchPerfEval/model/ResearchPerfEvalQuery';
import { ResearchPerfEvalCriteriaDataItem } from '@/module/ResearchPerfEval/model/ResearchPerfEvalTypes';

import { Content, DeleteButton } from '@/core/components';

import { ExternalSourceLink, FileCard, Id, useAppNotification } from '@feature/utils';

import { ResearchPerfEvalItemCardHeader } from './components/card/ResearchPerfEvalItemCardHeader';

interface ResearchPerfEvalCriteriaItemCardProps {
  item: ResearchPerfEvalCriteriaDataItem;
  index: number;

  evaluationId: Id;
  authorId: Id;
  orderNo: number;
  dataEntryHint: string | null;

  onEdit: (item: ResearchPerfEvalCriteriaDataItem) => void;
}

function useItemDelete(ResearchPerfEvalId: Id, authorId: Id, orderNo: number) {
  const { mutateAsync, isPending } = useResearchPerfEvalDeleteCriteriaItem(ResearchPerfEvalId, authorId, orderNo);
  const notification = useAppNotification();
  const onDelete = (itemId: Id) => {
    return mutateAsync(itemId).then(notification.success).catch(notification.error);
  };

  return {
    onDelete,
    isDeleting: isPending,
  };
}

export function ResearchPerfEvalCriteriaItemCard({
  evaluationId,
  authorId,
  orderNo,
  item,
  index,
  onEdit,
  dataEntryHint,
}: ResearchPerfEvalCriteriaItemCardProps) {
  const { onDelete, isDeleting } = useItemDelete(evaluationId, authorId, orderNo);
  const { t } = useTranslation();

  return (
    <Content
      flex={1}
      type="full"
    >
      <Flex justify="space-between">
        <Flex flex={1}>
          <Stack
            gap="2px"
            justify="center"
            align="center"
            mr="10px"
          >
            {index != null && <Text fw="500">#{index + 1}</Text>}
            <ActionIcon
              onClick={() => onEdit(item)}
              variant="filled"
              aria-label={t('button.edit')}
            >
              <EditIcon
                style={{ width: '70%', height: '70%' }}
                stroke={1.5}
              />
            </ActionIcon>
          </Stack>
          <Stack
            flex={1}
            gap="4px"
            mr="12px"
          >
            <ResearchPerfEvalItemCardHeader
              item={item.item}
              dataEntryHint={dataEntryHint}
              userId={authorId}
            />
            <Flex
              flex={1}
              justify="space-between"
            >
              <Stack gap="4px">
                {item.link && (
                  <ExternalSourceLink
                    url={item.link}
                    name={item.link}
                  />
                )}
                <Text
                  c="dimmed"
                  size="sm"
                  fs="italic"
                >
                  {item.note}
                </Text>
              </Stack>
            </Flex>
            {item.file && (
              <FileCard
                file={item.file}
                onDownload={() =>
                  window.open(getResearchPerfEvalItemFileDownloadUrl(evaluationId, authorId, orderNo, item.id))
                }
              />
            )}
          </Stack>
        </Flex>
        <Center>
          <DeleteButton
            size="xs"
            onDelete={() => onDelete(item.id)}
            isDeleting={isDeleting}
          />
        </Center>
      </Flex>
    </Content>
  );
}
